import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import { toast } from "react-toastify"
import { DateField } from '@mui/x-date-pickers'
import { noteTypes, unitStatusOptions, unitStatusLabelColors, unitTypes, epcLabelOptions } from '../../../assets/data/data'
import PopupDropdown from '../../PopupDropdown'
import { PopupWindow } from '../../PopupWindow'
import Note from '../../Note'
import httpClient from '../../helpers/httpClient'
import { formatDate, parseDateValue } from '../../helpers/formatDate'
import { isValidDateDayJS } from '../../helpers/globalFns'
import { showNotificationPopup } from '../../NotificationPopup'
import dayjs from 'dayjs'
import { getNoteCreatorName } from '../../helpers/getNoteCreatorName'

const GeneralSection = forwardRef(({ unit, setUnit, actionType, contactsList, loading, creatingNote, setCreatingNote }, ref) => {
  const [notes, setNotes] = useState([])
  const [noteDetailPopupIsActive, setNoteDetailPopupIsActive] = useState(false)
  const [selectedNote, setSelectedNote] = useState(null)
  const getEmptyNoteObj = () => ({
    noteDate: dayjs(),
    noteType: 'Note',
    contact: null,
    subject: '',
    content: ''
  })

  const [newNoteObj, setNewNoteObj] = useState(getEmptyNoteObj)
  const [editingNote, setEditingNote] = useState(null)

  const fetchNotes = async () => {
    if (!unit) return

    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/notes/get/${unit.id}?type=unit`)
    if (response.status == 200) {
      setNotes(response.data)
    }
  }

  useEffect(() => {
    if (unit) {
      fetchNotes()
    }
  }, [unit])

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    handleCreateNote: async () => {
      if (!isValidDateDayJS(newNoteObj?.noteDate)) {
        toast.error("Date not valid or is empty")
        return false
      }

      if (!newNoteObj.noteType || !newNoteObj.subject) {
        toast.error("Type and subject are required")
        return false
      }

      const data = {
        ...newNoteObj,
        contact: newNoteObj.contact?.id ? newNoteObj.contact : null
      }

      try {
        if (editingNote?.id) {
          await toast.promise(
            httpClient.put(import.meta.env.VITE_API_URL + `/api/notes/update/${editingNote.id}`, data),
            {
              success: `Note has been updated!`,
              error: "Something went wrong",
              pending: `Updating note...`
            }
          )
        } else {
          data["unitID"] = unit.id
          await toast.promise(
            httpClient.post(import.meta.env.VITE_API_URL + `/api/notes/add`, data),
            {
              success: `Note has been created!`,
              error: "Something went wrong",
              pending: `Creating note...`
            }
          )
        }

        setNewNoteObj(getEmptyNoteObj())
        setEditingNote(null)
        setCreatingNote(false)
        await fetchNotes()
        return true
      } catch (error) {
        return false
      }
    }
  }))

  const handleOnChangeInput = (e) => {
    const key = e.currentTarget.getAttribute("name")
    const value = e.currentTarget.value
    setUnit(prev => ({ ...prev, [key]: value }))
  }

  const handleOnChangeStatus = (value) => {
    setUnit(prev => ({ ...prev, status: value }))
  }

  const handleCreateNote = () => {
    setEditingNote(null)
    setNewNoteObj(getEmptyNoteObj())
    setCreatingNote(true)
  }

  const handleEditNote = (note) => {
    setEditingNote(note)
    setNewNoteObj({
      noteDate: parseDateValue(note?.date_note),
      noteType: note?.type || '',
      contact: note?.contact_id?.id ? note.contact_id : null,
      subject: note?.subject || '',
      content: note?.note || ''
    })
    setCreatingNote(true)
  }

  const setStateNewNote = (key, value) => {
    setNewNoteObj(prev => ({ ...prev, [key]: value }))
  }

  const handleSetStateNewNoteType = (value) => {
    setNewNoteObj(prev => ({ ...prev, noteType: value }))
  }

  const handleSetStateNewNoteContact = (value) => {
    setNewNoteObj(prev => ({ ...prev, contact: value }))
  }

  const handleOnChangeType = (value) => {
    setUnit(prev => ({ ...prev, type: value }))
  }

  const handleOnChangeEpcLabel = (value) => {
    setUnit(prev => ({ ...prev, EPC_label: value }))
  }

  const handleDeleteNote = (note) => {
    if (!note?.id) {
      return
    }

    showNotificationPopup({
      message: 'Are you sure you want to delete this note? This action cannot be undone.',
      cancelButton: { text: 'Cancel', color: 'var(--secondairy-light-medium-gray)' },
      actionButton: { text: 'Delete', color: 'var(--dark-red)' },
      onAction: async () => {
        try {
          await toast.promise(
            httpClient.delete(import.meta.env.VITE_API_URL + `/api/notes/delete/${note.id}`),
            {
              pending: 'Deleting note...',
              success: 'Note deleted 👍',
              error: 'Delete failed 😢'
            }
          )

          if (selectedNote?.id === note.id) {
            setSelectedNote(null)
            setNoteDetailPopupIsActive(false)
          }

          fetchNotes()
        } catch (error) {
          const apiMsg = error?.response?.data?.message
          toast.error(apiMsg || 'Cannot delete note.')
        }
      }
    })
  }

  return (
    <div className={`popout_body__content_container ${actionType == 'add' ? "small" : ""}`} id="unit-general-section">
      <div className='panel'>
        <h2>Details</h2>
        <div className='form__section no-border no-padding'>
          <div className='form__section'>
            <div className='form__section--content'>
              <div className="input_field--combo-grid">
                <label className="input_field__label" htmlFor="unit_name">Status</label>
                <PopupDropdown
                  options={unitStatusOptions}
                  value={unit?.status}
                  onChange={handleOnChangeStatus}
                  title={"Select status"}
                  labelView={true}
                  labelColors={unitStatusLabelColors}
                />
              </div>
              <div className="input_field--combo-grid">
                <label className="input_field__label" htmlFor="unit_name">Unit name</label>
                <input
                  className="input_field__input_box--text"
                  id="unit_name"
                  type="text"
                  name="unit_name"
                  value={unit?.unit_name}
                  onChange={handleOnChangeInput}
                />
              </div>
              <div className="input_field--combo-grid">
                <label className="input_field__label" htmlFor="unit_name">Type</label>
                <PopupDropdown
                  searchBar={true}
                  options={unitTypes}
                  value={unit?.type}
                  onChange={handleOnChangeType}
                  title={"Select unit type"}
                />
              </div>
              <div className="input_field--combo-grid">
                <label className="input_field__label" htmlFor="unit_address">Address</label>
                <input
                  className="input_field__input_box--text"
                  id="unit_address"
                  type="text"
                  name="address"
                  value={unit?.address}
                  onChange={handleOnChangeInput}
                />
              </div>
              <div className="input_field--combo-grid">
                <label className="input_field__label" htmlFor="epc_label">EPC Label</label>
                <PopupDropdown
                  options={epcLabelOptions}
                  value={unit?.EPC_label}
                  onChange={handleOnChangeEpcLabel}
                  title={"Select EPC label"}
                />
              </div>
              <div className="input_field--combo-grid">
                <label className="input_field__label" htmlFor="habitable_area_m2">Habitable area (m²)</label>
                <input
                  className="input_field__input_box--text"
                  id="habitable_area_m2"
                  type="number"
                  step="0.01"
                  name="habitable_area_m2"
                  value={unit?.habitable_area_m2 ?? ''}
                  onChange={handleOnChangeInput}
                />
              </div>
              <div className="input_field--combo-grid">
                <label className="input_field__label" htmlFor="terrace_area_m2">Terrace area (m²)</label>
                <input
                  className="input_field__input_box--text"
                  id="terrace_area_m2"
                  type="number"
                  step="0.01"
                  name="terrace_area_m2"
                  value={unit?.terrace_area_m2 ?? ''}
                  onChange={handleOnChangeInput}
                />
              </div>
              <div className="input_field--combo-grid">
                <label className="input_field__label" htmlFor="garden_area_m2">Garden area (m²)</label>
                <input
                  className="input_field__input_box--text"
                  id="garden_area_m2"
                  type="number"
                  step="0.01"
                  name="garden_area_m2"
                  value={unit?.garden_area_m2 ?? ''}
                  onChange={handleOnChangeInput}
                />
              </div>
              <div className="input_field--combo-grid">
                <label className="input_field__label" htmlFor="floor_level">Floor level</label>
                <input
                  className="input_field__input_box--text"
                  id="floor_level"
                  type="number"
                  step="1"
                  name="floor_level"
                  value={unit?.floor_level ?? ''}
                  onChange={handleOnChangeInput}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {actionType != 'add' && (
        <div className='panel'>
          <h2>Notes</h2>
          <div className='form__section'>
            <div className='form__section--content'>
              <PopupWindow
                popupID={"popup-unit-notes-create"}
                isActive={creatingNote}
                setIsActive={setCreatingNote}
              >
                <div className='note__container--popup'>
                  <div className='form__section no-border no-padding'>
                    <div className="input_field--combo-grid">
                      <label className="input_field__label" htmlFor="note_date">Note date</label>
                      <DateField
                        className="date-picker"
                        format="DD-MM-YYYY"
                        value={newNoteObj.noteDate}
                        onChange={(value) => {
                          // Set the time to noon to avoid timezone issues
                          if (value) {
                            value = value.hour(12).minute(0).second(0)
                          }
                          setStateNewNote("noteDate", value)
                        }}
                        slotProps={{
                          textField: {
                            inputProps: {
                              placeholder: "DD-MM-YYYY"
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="input_field--combo-grid">
                      <label className="input_field__label" htmlFor="note_type">Note type</label>
                      <PopupDropdown
                        options={Object.keys(noteTypes)}
                        value={newNoteObj.noteType}
                        onChange={handleSetStateNewNoteType}
                        title={"Select note type"}
                      />
                    </div>
                    <div className="input_field--combo-grid">
                      <label className="input_field__label" htmlFor="note_type">Contact (optional)</label>
                      <PopupDropdown
                        options={contactsList}
                        value={newNoteObj.contact}
                        onChange={handleSetStateNewNoteContact}
                        title={"Select contact"}
                        searchBar={true}
                      />
                    </div>
                    <div className="input_field--combo-grid">
                      <label className="input_field__label" htmlFor="note_subject">Subject</label>
                      <input
                        className="input_field__input_box--text"
                        id="note_subject"
                        type="text"
                        name="note_subject"
                        value={newNoteObj.subject || ''}
                        onChange={(e) => setStateNewNote("subject", e.currentTarget.value)}
                      />
                    </div>
                    <div className="input_field">
                      <label className="input_field__label" htmlFor="note_content">Content</label>
                      <textarea
                        className="input_field__input_box--text-area"
                        id="note_content"
                        type="text"
                        name="note_content"
                        value={newNoteObj.content || ''}
                        onChange={(e) => setStateNewNote("content", e.currentTarget.value)}
                      />
                    </div>
                  </div>
                </div>
              </PopupWindow>

              <PopupWindow
                popupID={"popup-unit-notes-detail"}
                isActive={noteDetailPopupIsActive}
                setIsActive={setNoteDetailPopupIsActive}
              >
                {selectedNote && (
                  <div className='note__container--popup note__container--popup--detail'>
                    <div className='date_note--popup'>
                      <div className='popup_window__subtitle'>Date:</div>
                      <p>{formatDate(selectedNote.date_note)}</p>
                    </div>
                    <div className='date_note--popup'>
                      <div className='popup_window__subtitle'>Created by:</div>
                      <p>{getNoteCreatorName(selectedNote)}</p>
                    </div>
                    <div className='date_note--popup'>
                      <div className='popup_window__subtitle'>Contact:</div>
                      {selectedNote.contact_id ? (
                        <p>{selectedNote.contact_id.first_name} {selectedNote.contact_id.last_name}</p>
                      ) : selectedNote.company_id ? (
                        <p>{selectedNote.company_id.company_name} (company)</p>
                      ) : (
                        <p>-</p>
                      )}
                    </div>
                    <div className='date_note--popup'>
                      <div className='popup_window__subtitle'>Subject:</div>
                      <p>{selectedNote.subject}</p>
                    </div>
                    <div className='note_text--popup'>
                      <div className='popup_window__subtitle'>Note:</div>
                      <p>{selectedNote.note}</p>
                    </div>
                  </div>
                )}
              </PopupWindow>

              <button className='more--btn no-margin' onClick={handleCreateNote}>+</button>
              {notes.map((note, index) => (
                <Note
                  key={index}
                  popupWindowID="popup-contact-notes"
                  noteObj={note}
                  type={note.type}
                  note={note.note}
                  subject={note.subject}
                  date={note.date_note}
                  setSelectedNote={setSelectedNote}
                  setPopupActive={setNoteDetailPopupIsActive}
                  onDelete={handleDeleteNote}
                  onEdit={handleEditNote}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
})

GeneralSection.displayName = 'GeneralSection'
export default GeneralSection
